import { useProductDetails } from '@/data/Content/ProductDetails';
import { useLocalization } from '@/data/Localization';
import { ProductAttributeValue } from '@/data/types/Product';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';

import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Grid,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { uniqWith } from 'lodash';
import { FC } from 'react';

import {
	accordionSummary,
	attributeNameBox,
	attributeValueBox,
	constantGridItemProps,
	constantGridProps,
	contentBoxTemplate,
	headerSpecs,
} from '@/components/content/HdmSpecs/styles/HdmSpecsStyle';
import {
	NAVIGABLE_SECTION_IDS,
	useNavigableAccordionSection,
} from '@/components/content/ProductDetails/hooks/useNavigableAccordionSection';
import { WidgetComponentProps } from '@/data/types/Slot';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { OpenSansFontWeights } from 'shared/custom/constants/commonConstants';

const comparator = (l: ProductAttributeValue, r: ProductAttributeValue) => l.value === r.value;
const emptyAttrValues: ProductAttributeValue[] = [];

const capitalizeFirstLetter = (data: string): string | undefined => {
	let newData = data;
	if (!newData || newData.trim() === '') return undefined;
	newData = newData.trim().toLowerCase();
	return newData.charAt(0).toUpperCase() + newData.slice(1);
};

export const HdmSpecs: FC<WidgetComponentProps> = ({ id }) => {
	const navigableSectionProps = useNavigableAccordionSection({
		sectionId: NAVIGABLE_SECTION_IDS.PRODUCT_SPECS,
	});
	const isExpanded = navigableSectionProps.expanded;

	const { TechnicalSpecifications } = useLocalization('HDMProductDetail');
	const { selection, product, isBuyable } = useProductDetails({
		partNumber: id.toString(),
	});
	const theme = useTheme();
	const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

	const {
		tabsData: { descriptiveAttributes },
	} = getProductDisplayInfo(selection?.sku, product);

	let dynamicColumns = 1;
	if (isMdUp) {
		dynamicColumns = 2;
	}
	const COLUMNS = dynamicColumns;

	if (!product || !isBuyable) {
		return null;
	}

	return (
		<Accordion sx={headerSpecs} disableGutters {...navigableSectionProps}>
			<AccordionSummary
				sx={accordionSummary}
				expandIcon={isExpanded ? <RemoveIcon /> : <AddIcon />}
			>
				<Typography variant="headingSegment" component="h3">
					{TechnicalSpecifications.t()}
				</Typography>
			</AccordionSummary>
			<AccordionDetails>
				<Grid
					container
					data-testid="product-details-grid"
					id="product-details-grid"
					spacing={constantGridProps.spacing}
				>
					{descriptiveAttributes.map(({ identifier, name, values = emptyAttrValues }, index) => {
						const isOddRow = Math.floor(index / COLUMNS) % 2 !== 0;
						const rowBackgroundColor = isOddRow
							? theme.palette.common.white
							: theme.palette.grey[50];

						const attributeValue = uniqWith(values, comparator)
							.map((v) => v.value)
							.join(', ');

						const displayAttributeName = capitalizeFirstLetter(name) || name;
						const displayAttributeValue = capitalizeFirstLetter(attributeValue) || attributeValue;

						return (
							<Grid
								item
								xs={constantGridItemProps.xs}
								md={constantGridItemProps.md}
								key={`product-attribute-${identifier}`}
							>
								<Box
									sx={{
										...contentBoxTemplate,
										backgroundColor: rowBackgroundColor,
									}}
								>
									<Box sx={attributeNameBox}>
										<Typography
											variant="body1"
											component="p"
											fontWeight={OpenSansFontWeights.semibold}
										>
											{displayAttributeName}
										</Typography>
									</Box>

									<Box sx={attributeValueBox}>
										<Typography variant="body1" component="p">
											{displayAttributeValue}
										</Typography>
									</Box>
								</Box>
							</Grid>
						);
					})}
				</Grid>
			</AccordionDetails>
		</Accordion>
	);
};
